/**
 * GTM Lead Engine — shared contracts for the internal admin CRM slice.
 *
 * Org-wide internal lead pool (Vestra GTM team), served by apps/server
 * admin-gtm routes and rendered in apps/dashboard /admin/gtm. Milestone 1:
 * Apollo import → enrich/tier → AI draft → send (Composio) or manual queue.
 */

import type { GtmProspectQuery } from '../schemas/gtm.schema.js';

/** GTM dashboard sections gatable per staff member via admin_roles.gtm_permissions. */
export const GTM_SECTIONS = ['leads', 'content', 'discovery', 'ops'] as const;
export type GtmSection = (typeof GTM_SECTIONS)[number];

export type GtmLeadStage =
  | 'new'
  | 'enriched'
  | 'drafted'
  | 'queued'
  | 'sent'
  | 'replied'
  | 'demo_booked'
  | 'won'
  | 'lost'
  | 'skipped';

export type GtmTier = 'A' | 'B' | 'C' | 'D';

export type GtmChannel = 'linkedin' | 'twitter' | 'email';

export type GtmMessageStatus = 'draft' | 'queued_manual' | 'sending' | 'sent' | 'failed';

export type GtmSendAdapter = 'composio' | 'manual';

export type GtmImportStatus = 'running' | 'ready' | 'error';

export type GtmActivityType = 'import' | 'enrich' | 'draft' | 'send' | 'status_change';

/** Behavioral engagement signal types, strongest intent first. */
export type GtmSignalType = 'magnet_request' | 'replied' | 'commented' | 'liked' | 'followed';

export type GtmSignalPlatform = 'linkedin' | 'x';

// -----------------------------------------------------------------------------
// Sub-segment atlas (from the global GTM playbook) — the targeting layer.
// A lead carries one `sub_segment`; its ICP and beachhead status DERIVE from the
// reference below (single source of truth), so they never drift. `unclassified`
// is the default when we can't confidently tag a lead yet.
// -----------------------------------------------------------------------------

/** The two demand pools. */
export type GtmIcp = 'icp1' | 'icp2';

/** Spend-priority label, derived from the sub-segment. */
export type GtmBeachheadStatus = 'finalized' | 'wave2' | 'seed';

export type GtmSubSegment =
  | 'stack_hopping_solopreneur'
  | 'automation_agency_operator'
  | 'ai_educator_creator'
  | 'ai_forward_employee'
  | 'marketing_agency_founder'
  | 'd2c_ecommerce_operator'
  | 'bookkeeping_accounting_owner'
  | 'nontechnical_founder'
  | 'brokerage_real_estate'
  | 'clinic_wellness_owner'
  | 'unclassified';

export interface GtmSubSegmentDef {
  value: GtmSubSegment;
  label: string;
  /** null only for `unclassified`. */
  icp: GtmIcp | null;
  /** null only for `unclassified`. */
  status: GtmBeachheadStatus | null;
}

/** The atlas as data — dropdowns, drafting track selection, and mix reports all read this. */
export const GTM_SUB_SEGMENTS: readonly GtmSubSegmentDef[] = [
  { value: 'stack_hopping_solopreneur', label: 'Stack-hopping solopreneur', icp: 'icp1', status: 'finalized' },
  { value: 'automation_agency_operator', label: 'Automation agency operator', icp: 'icp1', status: 'finalized' },
  { value: 'ai_educator_creator', label: 'AI educator / creator', icp: 'icp1', status: 'finalized' },
  { value: 'ai_forward_employee', label: 'AI-forward employee', icp: 'icp1', status: 'seed' },
  { value: 'marketing_agency_founder', label: 'Marketing / content agency founder', icp: 'icp2', status: 'finalized' },
  { value: 'd2c_ecommerce_operator', label: 'D2C / e-commerce operator', icp: 'icp2', status: 'finalized' },
  { value: 'bookkeeping_accounting_owner', label: 'Bookkeeping / accounting owner', icp: 'icp2', status: 'wave2' },
  { value: 'nontechnical_founder', label: 'Non-technical founder (umbrella)', icp: 'icp2', status: 'wave2' },
  { value: 'brokerage_real_estate', label: 'Brokerage / real-estate principal', icp: 'icp2', status: 'wave2' },
  { value: 'clinic_wellness_owner', label: 'Clinic / wellness owner', icp: 'icp2', status: 'wave2' },
  { value: 'unclassified', label: 'Unclassified', icp: null, status: null },
] as const;

const SUB_SEGMENT_BY_VALUE: Record<GtmSubSegment, GtmSubSegmentDef> = Object.fromEntries(
  GTM_SUB_SEGMENTS.map((s) => [s.value, s]),
) as Record<GtmSubSegment, GtmSubSegmentDef>;

/** The ICP a sub-segment belongs to (null for `unclassified`). */
export function icpForSubSegment(sub: GtmSubSegment): GtmIcp | null {
  return SUB_SEGMENT_BY_VALUE[sub]?.icp ?? null;
}

/** The spend-priority label for a sub-segment (null for `unclassified`). */
export function beachheadStatusForSubSegment(sub: GtmSubSegment): GtmBeachheadStatus | null {
  return SUB_SEGMENT_BY_VALUE[sub]?.status ?? null;
}

// -----------------------------------------------------------------------------
// One-click ICP prospecting — the 3 saved searches the "Run ICP prospecting"
// button fires in one go (validated against live Apollo). The connector is
// fixed by the batch runner ('apollo'); each query paginates to maxResults.
// -----------------------------------------------------------------------------

export interface GtmIcpPreset {
  key: string;
  label: string;
  /** Prospecting connector slug the batch runner fires this query through. */
  connector: string;
  /**
   * The sub-segment this preset deliberately targets — stamped on every
   * imported lead instead of re-guessing via inferSubSegment (which lands
   * "marketing agency founders" in unclassified when the title doesn't
   * spell out "agency").
   */
  subSegment: GtmSubSegment;
  query: GtmProspectQuery;
}

/** Per-preset outcome of the one-click ICP batch — failure must be distinguishable from "0 new". */
export interface GtmIcpPresetResult {
  key: string;
  label: string;
  importedCount: number;
  outcome: 'imported' | 'failed';
  /** Set when outcome='failed' — short operator-facing reason. */
  error?: string;
}

export const GTM_ICP_PROSPECT_PRESETS: readonly GtmIcpPreset[] = [
  {
    key: 'marketing_agency',
    label: 'Marketing agency founders',
    connector: 'apollo',
    subSegment: 'marketing_agency_founder',
    query: {
      titles: ['Founder', 'Co-Founder', 'CEO', 'Owner', 'Managing Director'],
      seniorities: ['owner', 'founder', 'c_suite'],
      keywords: 'marketing agency',
      companySizes: ['1,10', '11,20', '21,50'],
      locations: ['United States', 'India', 'United Kingdom'],
      perPage: 25,
      maxResults: 150,
    },
  },
  {
    key: 'automation_agency',
    label: 'Automation agency operators',
    connector: 'apollo',
    subSegment: 'automation_agency_operator',
    query: {
      titles: ['Founder', 'Co-Founder', 'CEO'],
      seniorities: ['owner', 'founder'],
      keywords: 'automation agency',
      companySizes: ['1,10', '11,20'],
      locations: ['United States', 'India', 'United Kingdom'],
      perPage: 25,
      maxResults: 150,
    },
  },
  {
    key: 'd2c_ecommerce',
    label: 'D2C / e-commerce operators',
    connector: 'apollo',
    subSegment: 'd2c_ecommerce_operator',
    query: {
      titles: ['Founder', 'Co-Founder', 'CEO', 'COO', 'Head of Operations'],
      seniorities: ['owner', 'founder', 'c_suite'],
      keywords: 'ecommerce',
      companySizes: ['1,10', '11,20', '21,50'],
      locations: ['United States', 'India', 'United Kingdom'],
      perPage: 25,
      maxResults: 150,
    },
  },
];

// -----------------------------------------------------------------------------
// ICP-mix report — signup/lead mix by sub-segment (the "cell data, not opinion"
// instrument the cadence re-scores beachheads on).
// -----------------------------------------------------------------------------

export interface GtmIcpMixRow {
  subSegment: GtmSubSegment;
  label: string;
  icp: GtmIcp | null;
  status: GtmBeachheadStatus | null;
  count: number;
  /** Share of all leads, 0-100. */
  pct: number;
}

// -----------------------------------------------------------------------------
// PMF gate (playbook FIG. 14) — the design-partner cohort + manual metric
// snapshots that decide when scale spend unlocks. v1 is manual entry; a PostHog
// feed can replace the snapshot writes later.
// -----------------------------------------------------------------------------

export type GtmDesignPartnerStatus = 'prospect' | 'active' | 'dropped';

export interface GtmDesignPartner {
  id: string;
  name: string;
  company: string | null;
  subSegment: GtmSubSegment;
  status: GtmDesignPartnerStatus;
  /** Dreaded task done in under 15 min, session 1. */
  hitAha: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtmPmfSnapshot {
  id: string;
  seanEllisPct: number | null;
  d7RetentionPct: number | null;
  ahaPct: number | null;
  waitlistActivePct: number | null;
  tasksPerUserWeek: number | null;
  createdAt: string;
}

/** One gate metric tile — server-computed pass/fail vs its threshold. */
export interface GtmPmfMetric {
  key: 'seanEllis' | 'd7Retention' | 'aha' | 'waitlistActive' | 'tasksPerUserWeek';
  label: string;
  value: number | null;
  unit: '%' | '/wk';
  threshold: number;
  comparator: 'gte' | 'gt';
  /** false when the value is null (not yet measured). */
  pass: boolean;
  /** Directional metrics are shown but never block `cleared`. */
  directional: boolean;
}

export interface GtmPmfGate {
  metrics: GtmPmfMetric[];
  /** true only when every non-directional metric passes. */
  cleared: boolean;
  partnerCount: number;
  ahaCount: number;
}

/** GET /api/admin/gtm/pmf response. */
export interface GtmPmfBoard {
  partners: GtmDesignPartner[];
  snapshot: GtmPmfSnapshot | null;
  gate: GtmPmfGate;
}

/** One captured engagement event on a lead (append-only). */
export interface GtmSignal {
  type: GtmSignalType;
  platform: GtmSignalPlatform;
  /** Post URL/id the engagement happened on. */
  ref?: string;
  /** ISO timestamp the signal was captured. */
  at: string;
}

export interface GtmLead {
  id: string;
  importId: string | null;
  source: string;
  externalId: string | null;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  email: string | null;
  linkedinUrl: string | null;
  twitterUrl: string | null;
  location: string | null;
  companyDomain: string | null;
  companySize: number | null;
  industry: string | null;
  seniority: string | null;
  /** Apollo's verification state for `email` (e.g. 'verified'); null if unknown/never checked. */
  emailStatus: string | null;
  fitScore: number | null;
  intentScore: number | null;
  tier: GtmTier | null;
  /** Targeting: which of the 10 atlas profiles; drives the drafting track. */
  subSegment: GtmSubSegment;
  stage: GtmLeadStage;
  /** Behavioral engagement signals (warm-lead loop). Empty for cold leads. */
  signals: GtmSignal[];
  enrichedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtmLeadImport {
  id: string;
  importedBy: string | null;
  source: string;
  query: Record<string, unknown> | null;
  status: GtmImportStatus;
  requestedCount: number | null;
  importedCount: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtmMessage {
  id: string;
  leadId: string;
  channel: GtmChannel;
  status: GtmMessageStatus;
  body: string | null;
  model: string | null;
  personalization: Record<string, unknown> | null;
  sendAdapter: GtmSendAdapter | null;
  externalId: string | null;
  error: string | null;
  draftedBy: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtmActivity {
  id: string;
  leadId: string | null;
  importId: string | null;
  type: GtmActivityType;
  actor: string | null;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

/** Connector kinds the pipeline is pluggable on. */
export type GtmConnectorKind = 'prospecting' | 'enrichment' | 'send';

/** One entry in the connector catalog surfaced to the dashboard. */
export interface GtmConnectorInfo {
  slug: string;
  displayName: string;
  kinds: GtmConnectorKind[];
  configured: boolean;
  /** What unlocks it when not configured (e.g. "Set APOLLO_API_KEY"). */
  configureHint?: string;
}

/** Connector catalog + channel status surfaced to the dashboard. */
export interface GtmConnectorsStatus {
  connectors: GtmConnectorInfo[];
  liveSend: { enabled: boolean };
}

/** Per-operator usage rollup — "who burned how many vendor credits". */
export interface GtmOperatorUsage {
  actor: string;
  actorEmail: string | null;
  imports: number;
  enrichments: number;
  drafts: number;
  sends: number;
  /** Total vendor credits consumed across all connectors. */
  vendorCredits: number;
  /** Per-vendor breakdown, keyed by connector slug. */
  creditsByVendor: Record<string, number>;
}

// =============================================================================
// Apollo bulk enrich + waterfall email enrichment + usage panel
// =============================================================================

/** POST /api/admin/gtm/leads/bulk-enrich response. */
export interface GtmBulkEnrichResult {
  requested: number;
  enriched: number;
  noMatch: number;
  skipped: number;
  /** Leads never processed because a later Apollo chunk failed mid-run
   * (earlier chunks were applied — see `error`). */
  failed: number;
  /** Apollo's own summed credits_consumed for the run (authoritative). */
  creditsConsumed: number;
  /** Set when the run ended early on a chunk failure; partial results were kept. */
  error?: string;
}

/** POST /api/admin/gtm/leads/waterfall response — request(s) are in flight, async. */
export interface GtmWaterfallStartResult {
  requested: number;
  requestIds: string[];
  /** Leads never submitted because a later Apollo chunk failed mid-run. */
  failed?: number;
  /** Set when the run ended early on a chunk failure; earlier chunks are in flight. */
  error?: string;
}

/** POST /api/admin/gtm/waterfall/poll response. */
export interface GtmWaterfallPollResult {
  checked: number;
  completed: number;
  expired: number;
  failed: number;
}

/** GET /api/admin/gtm/apollo/usage response — degrades to `available: false`, never throws. */
export interface GtmApolloUsage {
  available: boolean;
  credits?: { used: number; limit: number; remaining?: number };
}

// =============================================================================
// Drip sequences (m2)
// =============================================================================

export type GtmEnrollmentStatus = 'active' | 'awaiting_send' | 'paused' | 'completed' | 'stopped';

/** One step of a drip sequence. Channel union widens to 'email' when Resend lands. */
export interface GtmSequenceStep {
  channel: GtmChannel;
  /** Days to wait after the previous step was SENT (0 = immediately on enroll). */
  delayDays: number;
  /** Optional guidance folded into the AI draft prompt for this step. */
  note?: string;
}

export interface GtmSequence {
  id: string;
  name: string;
  description: string | null;
  steps: GtmSequenceStep[];
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtmEnrollment {
  id: string;
  sequenceId: string;
  leadId: string;
  status: GtmEnrollmentStatus;
  currentStep: number;
  nextRunAt: string | null;
  pausedReason: string | null;
  enrolledBy: string | null;
  lastAdvancedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Enrollment joined with lead + sequence context for list/queue views. */
export interface GtmEnrollmentView extends GtmEnrollment {
  leadName: string | null;
  leadCompany: string | null;
  sequenceName: string;
  totalSteps: number;
}

/** One entry in the manual send queue: a sequence-drafted message awaiting send. */
export interface GtmQueueItem {
  message: GtmMessage;
  leadId: string;
  leadName: string | null;
  leadCompany: string | null;
  sequenceName: string | null;
  stepIndex: number | null;
  totalSteps: number | null;
}

/** Aggregate stats for the engagement panel. */
export interface GtmSequenceStats {
  sequenceId: string;
  sequenceName: string;
  enrolled: number;
  active: number;
  awaitingSend: number;
  paused: number;
  completed: number;
  stopped: number;
  replied: number;
}

// =============================================================================
// GTM Ops board — weekly loop, launch calendar, directory sweep
// =============================================================================

export type GtmLaunchStatus = 'idea' | 'prepping' | 'launched' | 'relaunch';

export interface GtmLaunch {
  id: string;
  title: string;
  hook: string | null;
  launchDate: string | null;
  status: GtmLaunchStatus;
  channel: string | null;
  notes: string | null;
  results: Record<string, unknown> | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type GtmDirectoryStatus = 'todo' | 'submitted' | 'listed' | 'rejected';

export type GtmDiscoveryCategory = 'ai_directory' | 'launch_platform' | 'review_site' | 'general';
export type GtmDiscoveryPricingModel = 'free' | 'freemium' | 'paid';

/** What a platform's submission form actually asks for. */
export interface GtmDirectoryRequirements {
  needsAccount?: boolean | null;
  taglineMax?: number | null;
  descMax?: number | null;
  logoSpec?: string | null;
  reviewDays?: string | null;
  notes?: string | null;
}

export interface GtmDirectory {
  id: string;
  name: string;
  url: string | null;
  status: GtmDirectoryStatus;
  submittedAt: string | null;
  notes: string | null;
  updatedBy: string | null;
  // Discovery extension (migration 20261013120000)
  category: GtmDiscoveryCategory;
  pricingModel: GtmDiscoveryPricingModel | null;
  priceUsd: number | null;
  pricingNotes: string | null;
  trafficEstimate: number | null;
  domainRating: number | null;
  submitUrl: string | null;
  /** Our live listing once status = listed. */
  listingUrl: string | null;
  requirements: GtmDirectoryRequirements | null;
  lastResearchedAt: string | null;
  researchNotes: string | null;
}

/** Directory row + the computed popularity-per-dollar score. */
export interface GtmDiscoveryPlatform extends GtmDirectory {
  valueScore: number | null;
}

/** The canonical Vestra listing reused for every submission (single row). */
export interface GtmListingProfile {
  productName: string;
  tagline: string;
  descriptionShort: string;
  descriptionMedium: string;
  descriptionLong: string;
  categoryTags: string[];
  websiteUrl: string;
  logoUrl: string | null;
  screenshotUrls: string[];
  videoUrl: string | null;
  contactEmail: string | null;
  socialLinks: Record<string, string> | null;
  pricingBlurb: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** One item of the weekly execution loop (definitions in GTM_WEEKLY_LOOP). */
export interface GtmWeeklyItem {
  key: string;
  day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri';
  label: string;
}

/**
 * The weekly execution loop from docs/gtm/execution-guidelines.md §8.
 * Completions are stored per ISO week in gtm_weekly_completions.
 */
export const GTM_WEEKLY_LOOP: GtmWeeklyItem[] = [
  { key: 'mon_trend_scan', day: 'Mon', label: 'Trend scan: viral posts in niche (last 7 days)' },
  { key: 'mon_content_drafts', day: 'Mon', label: 'Draft 2 trend-jack posts + 1 lead magnet' },
  { key: 'tue_aeo_pages', day: 'Tue', label: 'AEO: ship 1-2 use-case pages; llms.txt/docs health' },
  { key: 'tue_reddit', day: 'Tue', label: '1 Reddit value-post (never debate haters)' },
  { key: 'wed_warm_leads', day: 'Wed', label: 'Refresh warm-lead list from last week\'s engagement' },
  { key: 'wed_outbound', day: 'Wed', label: 'Queue DMs: enroll warm Tier A/B into sequences' },
  { key: 'thu_schedule_posts', day: 'Thu', label: 'Schedule week\'s LinkedIn/X posts' },
  { key: 'thu_ugc', day: 'Thu', label: 'Brief/collect UGC batch' },
  { key: 'fri_launch_prep', day: 'Fri', label: 'Advance next launch (video, hook variants, network brief)' },
  { key: 'fri_metrics', day: 'Fri', label: 'Metrics review (signups, demos, replies, channel impressions)' },
  { key: 'fri_reply_audit', day: 'Fri', label: 'Replied to EVERY comment on every post this week (Gojiberry rule)' },
];

export interface GtmWeeklyState {
  week: string;
  completions: Array<{ itemKey: string; doneBy: string | null; doneByEmail: string | null; doneAt: string }>;
}

/** GTM-native metrics summary for the ops board header. */
export interface GtmOpsMetrics {
  totalLeads: number;
  warmLeads: number;
  tierA: number;
  queuedSends: number;
  sentThisWeek: number;
  repliesThisWeek: number;
  liveEnrollments: number;
  /** Funnel bottom (Gojiberry: demos are the metric that matters). */
  demosBooked7d: number;
  won7d: number;
  /** Reply-speed SLA: replies paused for review ("speed compounds"). */
  repliesAwaitingReview: number;
  oldestReplyHours: number | null;
}

/** A registered lead magnet ("the offer is always a valuable blueprint"). */
export interface GtmLeadMagnet {
  id: string;
  title: string;
  hook: string | null;
  assetUrl: string | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Today's team send counts per channel vs the 30/account/day pace. */
export interface GtmSendPacing {
  /** Sends recorded today (UTC) keyed by channel. */
  today: Record<string, number>;
  /** Playbook benchmark: max sends per account per day. */
  perAccountDailyBenchmark: number;
}

export interface GtmLeadListResponse {
  leads: GtmLead[];
  total: number;
  page: number;
  limit: number;
}

export interface GtmLeadDetailResponse {
  lead: GtmLead;
  messages: GtmMessage[];
  activities: GtmActivity[];
}

// =============================================================================
// Filter-wide bulk email drafting
// =============================================================================

/** Dry-run counts shown before a bulk-draft run spends anything. */
export interface GtmBulkDraftPreview {
  /** Leads matching the caller's current filters. */
  total: number;
  /** Will actually be drafted (before the per-run cap). */
  eligible: number;
  /** Skipped: no email address to send to. */
  skippedNoEmail: number;
  /** Skipped: an email-channel message already exists (never double-charge). */
  skippedAlreadyDrafted: number;
}

/** Poll shape for a background bulk-draft job. */
export interface GtmBulkDraftJob {
  jobId: string;
  status: 'running' | 'finished';
  /** Leads queued in this run (post skip rules + per-run cap). */
  total: number;
  done: number;
  failed: number;
  startedAt: string;
  finishedAt: string | null;
  /** First few per-lead failure messages (capped server-side). */
  errors: string[];
}

// =============================================================================
// Content factory — viral-content snipes + influencer targets
// =============================================================================

export type GtmContentPlatform = 'x' | 'instagram' | 'linkedin' | 'tiktok' | 'youtube' | 'other';

export type GtmInfluencerStatus =
  | 'watch'
  | 'shortlisted'
  | 'contacted'
  | 'negotiating'
  | 'active'
  | 'rejected';

/** Structured AI assessment of an influencer as a marketing target. */
export interface GtmInfluencerAnalysis {
  niche: string;
  audienceFit: string;
  relevanceScore: number;
  pitchAngle: string;
  recommendation: 'shortlist' | 'watch' | 'skip';
}

export interface GtmInfluencer {
  id: string;
  platform: GtmContentPlatform;
  handle: string;
  name: string | null;
  profileUrl: string | null;
  followers: number | null;
  niche: string | null;
  relevanceScore: number | null;
  status: GtmInfluencerStatus;
  notes: string | null;
  analysis: GtmInfluencerAnalysis | null;
  /** How many of their posts we've sniped. */
  snipeCount?: number;
  createdAt: string;
  updatedAt: string;
}

export type GtmSnipeStatus = 'new' | 'analyzed' | 'planned' | 'used' | 'discarded';
export type GtmSnipeVerdict = 'use' | 'maybe' | 'skip';

/** Structured AI analysis of a sniped post. */
export interface GtmSnipeAnalysis {
  format: string;
  hookType: string;
  topic: string;
  viralFactors: string[];
  whyItWorks: string;
  /** Similarity to previously sniped content ("3rd snipe with this format"). */
  occurrenceNote: string;
  viralPotential: number;
  vestraAngle: string;
  suggestedAdaptation: string;
  verdict: GtmSnipeVerdict;
}

export interface GtmContentSnipe {
  id: string;
  url: string;
  platform: GtmContentPlatform;
  influencerId: string | null;
  influencerHandle?: string | null;
  pastedContent: string | null;
  /** Auto-fetched post text (X syndication / YouTube oEmbed; IG is login-walled). */
  crawledContent: string | null;
  crawlMeta: { source: string; fetchedAt: string; authorName: string | null } | null;
  metrics: Record<string, number> | null;
  status: GtmSnipeStatus;
  analysis: GtmSnipeAnalysis | null;
  viralScore: number | null;
  verdict: GtmSnipeVerdict | null;
  plannedDate: string | null;
  plannedChannel: string | null;
  notes: string | null;
  submittedBy: string | null;
  createdAt: string;
  updatedAt: string;
}
